import { ChangeDetectionStrategy, Component, computed, inject, input, forwardRef } from '@angular/core';
import { TABS_PARENT, TabsParent } from './tabs.token';

/**
 * A single panel inside an `<app-tabs>` container. Reads its active state from the
 * parent `TabsComponent` via injection and toggles its own host `[hidden]` binding,
 * so consumers don't need to write `*ngIf` or switch logic.
 *
 * @feature shared
 */
@Component({
  selector: 'app-tab-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.role]': '"tabpanel"',
    '[attr.id]': '"panel-" + tabId()',
    '[attr.aria-labelledby]': '"tab-" + tabId()',
    '[attr.tabindex]': 'isActive() ? 0 : -1',
    '[hidden]': '!isActive()',
  },
  template: `
    <ng-content />
  `,
})
export class TabPanelComponent {
  private readonly parent = inject<TabsParent>(TABS_PARENT, { optional: true });

  /** Unique id for this panel; matches `TabsComponent.activeTab`. */
  readonly tabId = input.required<string>();

  /** Visible label used by the parent to render the tab trigger. */
  readonly label = input.required<string>();

  /** Optional icon name (passed to `app-icon`) rendered before the label. */
  readonly icon = input<string | undefined>(undefined);

  /** Disables this tab. @default false */
  readonly disabled = input<boolean>(false);

  /** True when this panel is the currently selected tab. */
  readonly isActive = computed(() => this.parent?.activeTab() === this.tabId());
}
